import { supabase } from "./supabase";
import type { QueryClient } from "@tanstack/solid-query";
import { QueryKey } from "./query-keys";

// Subscribe to real-time Postgres changes and invalidate matching TanStack Query keys.
// Returns an unsubscribe function.
export function subscribeToTable(
  queryClient: QueryClient,
  table: string,
  queryKeys: QueryKey[],
): () => void {
  const channel = supabase
    .channel(`public:${table}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table },
      () => {
        for (const key of queryKeys) {
          queryClient.invalidateQueries({ queryKey: [key] });
        }
      },
    )
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}

// Subscribe to changes to a single user's row (filtered server-side by id).
// Used to keep the logged-in user's own role/profile live - e.g. a Discord
// promotion writes the row, this fires, and the auth store refetches. Returns an
// unsubscribe function. Inert in dev (anon realtime socket); refreshes on next
// login there instead.
export function subscribeToUserRow(userId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(`public:users:self:${userId}`)
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "users", filter: `id=eq.${userId}` },
      () => onChange(),
    )
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}

// Convenience: subscribe to all tables the UI depends on.
// When adding a new endpoint/query key, add the corresponding table here too - 
// AND give that table the `realtime_read` RLS policy in server/db/schema.ts.
// postgres_changes runs the RLS check as the authenticated subscriber; with RLS
// on (every table) and no SELECT policy, the check default-denies and nothing is
// delivered. The table must also be in Supabase's `supabase_realtime` publication
// (dashboard → Database → Publications) - that part isn't in migrations.
export function subscribeToAll(queryClient: QueryClient): () => void {
  const { Landing, Profile, Players, Rankings, Duels, Duel, Tournaments, Reports, Admin, Vouchers } = QueryKey;
  const unsubs = [
    subscribeToTable(queryClient, "users",                    [Players, Profile, Rankings, Landing]),
    subscribeToTable(queryClient, "duels",                    [Duels, Duel, Profile, Rankings]),
    subscribeToTable(queryClient, "duel_matches",             [Duels, Duel, Rankings, Profile]),
    subscribeToTable(queryClient, "duel_result_submissions",  [Duel, Duels]),
    subscribeToTable(queryClient, "rankings",                 [Rankings, Profile]),
    subscribeToTable(queryClient, "keppan",                   [Profile, Players, Rankings]),
    subscribeToTable(queryClient, "ranking_history",          [Rankings, Admin]),
    subscribeToTable(queryClient, "ranking_sync_log",         [Admin]),
    subscribeToTable(queryClient, "pending_ranking_changes",  [Admin, Rankings, Profile]),
    subscribeToTable(queryClient, "tournaments",              [Tournaments, Landing]),
    subscribeToTable(queryClient, "tournament_participants",  [Tournaments, Players]),
    subscribeToTable(queryClient, "tournament_races",         [Tournaments]),
    subscribeToTable(queryClient, "tournament_race_results",  [Tournaments, Rankings, Profile]),
    subscribeToTable(queryClient, "reports",                  [Reports, Admin]),
    subscribeToTable(queryClient, "tier_cooldowns",           [Rankings]),
    subscribeToTable(queryClient, "player_cooldowns",         [Rankings]),
    subscribeToTable(queryClient, "voucher_ledger",           [Vouchers, Profile]),
  ];
  return () => unsubs.forEach((u) => u());
}
